var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useAtom } from 'jotai';
import { useStore } from 'jotai/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useBoardHistory } from '@/hooks/useHistory';
import { serializeSelection, useHotkeys } from '@/hooks/useHotkeys';
import { roleAtom, selectedIdsAtom, stageRefAtom } from '@/store/boardAtoms';
import { clone, collectElements, diffElements, firstVisibleLayer, generateGroupId, getElementGroupId, getGroupElements, selectionLockState, sendHistoryStates, } from '@/utils/board/boardUtils';
import { draftStateAfterUpdate, moveSelectedElements } from '@/utils/board/stageUtils';
/**
 * Кастомный хук для обработки горячих клавиш на Stage
 *
 * @param params - Параметры хука
 */
export const useStageHotkeys = ({ boardState, setBoardState, selectedIds, visibleElements, canEdit, send, getInsertPosition, gridSize, snapToGrid, zoomIn, zoomOut, notifyActionBlocked, }) => {
    // Store для синхронного чтения атомов вне React-контекста
    const store = useStore();
    // Атомы состояния
    const [, setSelectedIds] = useAtom(selectedIdsAtom);
    const [role] = useAtom(roleAtom);
    // Состояние нажатых модификаторов
    const [isAltPressed, setIsAltPressed] = useState(false);
    const [isShiftPressed, setIsShiftPressed] = useState(false);
    const [isControlPressed, setIsControlPressed] = useState(false);
    const [isMetaPressed, setIsMetaPressed] = useState(false);
    // Состояние модалки загрузки
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [uploadDefaultFile, setUploadDefaultFile] = useState(null);
    const uploadResolverRef = useRef(null);
    // Состояние модалки множественной загрузки
    const [multiUploadModalOpen, setMultiUploadModalOpen] = useState(false);
    const [uploadDefaultFiles, setUploadDefaultFiles] = useState([]);
    const multiUploadResolverRef = useRef(null);
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Control')
                setIsControlPressed(true);
            if (e.key === 'Shift')
                setIsShiftPressed(true);
            if (e.key === 'Alt')
                setIsAltPressed(true);
            if (e.key === 'Meta')
                setIsMetaPressed(true);
        };
        const handleKeyUp = (e) => {
            if (e.key === 'Control')
                setIsControlPressed(false);
            if (e.key === 'Shift')
                setIsShiftPressed(false);
            if (e.key === 'Alt')
                setIsAltPressed(false);
            if (e.key === 'Meta')
                setIsMetaPressed(false);
        };
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, []);
    // Открывает модалку загрузки и возвращает результат
    const requestImageUpload = useCallback((file) => {
        setUploadDefaultFile(file);
        setUploadModalOpen(true);
        return new Promise((resolve) => {
            uploadResolverRef.current = resolve;
        });
    }, []);
    // Закрытие модалки кликом по фону/кресту — трактуем как отмену
    const onUploadModalClose = useCallback(() => {
        setUploadModalOpen(false);
        if (uploadResolverRef.current)
            uploadResolverRef.current(null);
    }, []);
    // Колбэк из UnifiedCloudinaryForm
    const onFormUploaded = useCallback((data) => {
        setUploadModalOpen(false);
        if (uploadResolverRef.current)
            uploadResolverRef.current(data);
    }, []);
    // Открывает модалку множественной загрузки и возвращает результат
    const requestMultipleImageUpload = useCallback((files) => {
        setUploadDefaultFiles(files);
        setMultiUploadModalOpen(true);
        return new Promise((resolve) => {
            multiUploadResolverRef.current = resolve;
        });
    }, []);
    // Закрытие модалки множественной загрузки
    const onMultiUploadModalClose = useCallback(() => {
        setMultiUploadModalOpen(false);
        if (multiUploadResolverRef.current)
            multiUploadResolverRef.current(null);
    }, []);
    // Колбэк из MultiUnifiedCloudinaryForm
    const onMultiFormUploaded = useCallback((data) => {
        setMultiUploadModalOpen(false);
        if (multiUploadResolverRef.current)
            multiUploadResolverRef.current(data);
    }, []);
    // Хук истории
    const { undoWithSnapshots, redoWithSnapshots, canUndo, canRedo } = useBoardHistory();
    // Debounced функция для отправки на сервер: отправляет только последний вызов
    const debounceTimerRef = useRef(null);
    const debouncedMoveSend = useCallback((payload) => {
        if (debounceTimerRef.current !== null) {
            window.clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = window.setTimeout(() => {
            send({ type: 'element.move', payload });
            debounceTimerRef.current = null;
        }, 300);
    }, [send]);
    /**
     * Обработчик нажатия Escape
     */
    const onEscape = useCallback(() => {
        setSelectedIds([]);
    }, [setSelectedIds]);
    /**
     * Обработчик увеличения z-index
     */
    const onZIndexUp = useCallback(() => {
        if (!canEdit)
            return;
        if (!selectedIds.length)
            return;
        const newState = clone(boardState);
        // Для каждого выбранного элемента увеличиваем zIndex на 1
        selectedIds.forEach((id) => {
            var _a;
            for (const layer of newState.layers) {
                const el = layer.elements.find((e) => e.id === id);
                if (el) {
                    const elIndex = (_a = el.zIndex) !== null && _a !== void 0 ? _a : 0;
                    el.zIndex = elIndex < -1 ? 0 : elIndex + 1;
                    // Синхронизируем props.zIndex с el.zIndex
                    el.props = Object.assign(Object.assign({}, el.props), { zIndex: el.zIndex });
                    send({
                        type: 'element.update',
                        payload: [{ id, props: { zIndex: el.zIndex }, zIndex: el.zIndex }],
                    });
                }
            }
        });
        setBoardState(newState);
    }, [selectedIds, boardState, send, setBoardState, canEdit]);
    /**
     * Обработчик уменьшения z-index
     */
    const onZIndexDown = useCallback(() => {
        if (!canEdit)
            return;
        if (!selectedIds.length)
            return;
        const newState = clone(boardState);
        // Для каждого выбранного элемента уменьшаем zIndex на 1
        selectedIds.forEach((id) => {
            var _a;
            for (const layer of newState.layers) {
                const el = layer.elements.find((e) => e.id === id);
                if (el) {
                    const elIndex = (_a = el.zIndex) !== null && _a !== void 0 ? _a : 0;
                    el.zIndex = elIndex - 1;
                    // Синхронизируем props.zIndex с el.zIndex
                    el.props = Object.assign(Object.assign({}, el.props), { zIndex: el.zIndex });
                    send({
                        type: 'element.update',
                        payload: [{ id, props: { zIndex: el.zIndex }, zIndex: el.zIndex }],
                    });
                }
            }
        });
        setBoardState(newState);
    }, [selectedIds, boardState, send, setBoardState, canEdit]);
    /**
     * Обработчик удаления элементов
     * Запрещает удаление заблокированных элементов и презентационных фреймов
     */
    const onDelete = useCallback(() => {
        if (!canEdit)
            return;
        if (!selectedIds.length)
            return;
        const elementsMap = collectElements(boardState);
        const deletableIds = [];
        for (const id of selectedIds) {
            const element = elementsMap.get(id);
            if (element && !element.locked && !element.isPresentationFrame) {
                deletableIds.push(id);
            }
        }
        if (deletableIds.length === 0) {
            notifyActionBlocked('delete');
            return;
        }
        setSelectedIds([]);
        send({ type: 'element.delete', payload: deletableIds.map((id) => ({ id })) });
    }, [selectedIds, boardState, send, setSelectedIds, canEdit, notifyActionBlocked]);
    /**
     * Обработчик копирования элементов
     *
     * Сохраняет относительные смещения выбранных элементов от центра трансформера
     * (центра bounding-box выделения) для восстановления взаимного позиционирования при вставке.
     */
    const onCopy = useCallback(() => {
        if (!canEdit)
            return;
        if (!selectedIds.length)
            return;
        const map = collectElements(boardState);
        const elements = selectedIds.map((id) => map.get(id)).filter(Boolean);
        if (!elements.length)
            return;
        const xs = elements.map((el) => (typeof el.props.x === 'number' ? el.props.x : 0));
        const ys = elements.map((el) => (typeof el.props.y === 'number' ? el.props.y : 0));
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        const maxX = Math.max(...elements.map((el, i) => {
            const w = typeof el.props.w === 'number' ? el.props.w : 0;
            return xs[i] + w;
        }));
        const maxY = Math.max(...elements.map((el, i) => {
            const h = typeof el.props.h === 'number' ? el.props.h : 0;
            return ys[i] + h;
        }));
        const centerX = minX + (maxX - minX) / 2;
        const centerY = minY + (maxY - minY) / 2;
        const payload = {
            kind: 'elements-with-offsets',
            items: elements.map((el) => {
                const offset = {
                    dx: (typeof el.props.x === 'number' ? el.props.x : 0) - centerX,
                    dy: (typeof el.props.y === 'number' ? el.props.y : 0) - centerY,
                };
                return {
                    element: el,
                    offset,
                };
            }),
        };
        try {
            navigator.clipboard.writeText(serializeSelection(payload));
        }
        catch (_a) {
            /* ignore clipboard errors */
        }
    }, [selectedIds, boardState, canEdit]);
    /**
     * Обработчик вставки элементов
     *
     * Поддерживает два формата payload:
     * - Массив элементов (старый формат) — вставляет каждый в точку вставки по центру
     * - ElementsCopyPayload — новый формат с сохранением относительных позиций
     *   восстанавливает взаимные позиции относительно точки вставки, как 0,0
     */
    const onPasteElements = useCallback((data) => {
        var _a;
        if (!canEdit)
            return;
        const firstLayer = firstVisibleLayer(boardState);
        if (!firstLayer)
            return;
        const { x: insertX, y: insertY } = getInsertPosition();
        const isArrayPayload = Array.isArray(data);
        const items = isArrayPayload
            ? data.map((el) => ({ element: el }))
            : ((_a = data.items) !== null && _a !== void 0 ? _a : []);
        if (!items.length)
            return;
        items.forEach(({ element, offset }) => {
            var _a, _b;
            const dx = (_a = offset === null || offset === void 0 ? void 0 : offset.dx) !== null && _a !== void 0 ? _a : 0;
            const dy = (_b = offset === null || offset === void 0 ? void 0 : offset.dy) !== null && _b !== void 0 ? _b : 0;
            const x = insertX + dx;
            const y = insertY + dy;
            const props = Object.assign(Object.assign({}, element.props), { x,
                y });
            send({
                type: 'element.add',
                payload: {
                    layerId: element.layerId,
                    elementType: element.elementType,
                    asset: element.asset || {},
                    props,
                },
            });
        });
    }, [canEdit, boardState, getInsertPosition, send]);
    /**
     * Обработчик вставки текста
     */
    const onPasteText = useCallback((text) => {
        if (!canEdit)
            return;
        const firstLayer = firstVisibleLayer(boardState);
        if (!firstLayer)
            return;
        const { x: centerX, y: centerY } = getInsertPosition();
        const props = { x: centerX, y: centerY, w: 200, h: 50, text, fontSize: 18 };
        send({
            type: 'element.add',
            payload: { layerId: firstLayer.id, elementType: 'text', props },
        });
    }, [boardState, getInsertPosition, send, canEdit]);
    /**
     * Обработчик вставки изображения
     */
    const onPasteImage = useCallback((file) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (!canEdit)
            return;
        const firstLayer = firstVisibleLayer(boardState);
        if (!firstLayer)
            return;
        const { x: centerX, y: centerY } = getInsertPosition();
        // Открываем форму загрузки с подставленным файлом
        const uploadedData = yield requestImageUpload(file);
        if (!uploadedData)
            return; // пользователь отменил
        const width = (_a = uploadedData.width) !== null && _a !== void 0 ? _a : 0;
        const height = (_b = uploadedData.height) !== null && _b !== void 0 ? _b : 0;
        const asset = {
            url: uploadedData.secure_url,
            meta: {
                type: file.type,
                format: uploadedData.format,
                displayName: uploadedData.display_name,
                publicId: uploadedData.public_id,
                w: width,
                h: height,
            },
        };
        send({
            type: 'element.add',
            payload: {
                layerId: firstLayer.id,
                elementType: 'image',
                asset,
                props: {
                    x: centerX - width / 2,
                    y: centerY - height / 2,
                    w: width,
                    h: height,
                },
            },
        });
    }), [boardState, getInsertPosition, send, requestImageUpload, canEdit]);
    /**
     * Обработчик вставки множественных изображений
     */
    const onPasteImages = useCallback((files) => __awaiter(void 0, void 0, void 0, function* () {
        if (!canEdit)
            return;
        const firstLayer = firstVisibleLayer(boardState);
        if (!firstLayer)
            return;
        const { x: centerX, y: centerY } = getInsertPosition();
        // Открываем форму множественной загрузки с подставленными файлами
        const uploadedDataArray = yield requestMultipleImageUpload(files);
        if (!uploadedDataArray || uploadedDataArray.length === 0)
            return; // пользователь отменил
        // Добавляем изображения на доску с небольшим смещением, чтобы они не накладывались друг на друга
        const offsetX = 50;
        const offsetY = 50;
        uploadedDataArray.forEach((uploadedData, index) => {
            var _a, _b, _c;
            const width = (_a = uploadedData.width) !== null && _a !== void 0 ? _a : 0;
            const height = (_b = uploadedData.height) !== null && _b !== void 0 ? _b : 0;
            const asset = {
                url: uploadedData.secure_url,
                meta: {
                    type: ((_c = files[index]) === null || _c === void 0 ? void 0 : _c.type) || 'image/jpeg',
                    format: uploadedData.format,
                    displayName: uploadedData.display_name,
                    publicId: uploadedData.public_id,
                    w: width,
                    h: height,
                },
            };
            send({
                type: 'element.add',
                payload: {
                    layerId: firstLayer.id,
                    elementType: 'image',
                    asset,
                    props: {
                        x: centerX - width / 2 + (index % 3) * offsetX,
                        y: centerY - height / 2 + Math.floor(index / 3) * offsetY,
                        w: width,
                        h: height,
                    },
                },
            });
        });
    }), [boardState, getInsertPosition, send, requestMultipleImageUpload, canEdit]);
    /**
     * Обработчик отмены действия
     */
    const onUndo = useCallback(() => {
        if (!canEdit)
            return;
        if (canUndo) {
            const { before, after } = undoWithSnapshots();
            const { added, removed, updated } = diffElements(before, after);
            sendHistoryStates(added, removed, updated, send);
        }
    }, [canUndo, undoWithSnapshots, send, canEdit]);
    /**
     * Обработчик повтора действия
     */
    const onRedo = useCallback(() => {
        if (!canEdit)
            return;
        if (canRedo) {
            const { before, after } = redoWithSnapshots();
            const { added, removed, updated } = diffElements(before, after);
            sendHistoryStates(added, removed, updated, send);
        }
    }, [canRedo, redoWithSnapshots, send, canEdit]);
    /**
     * Обработчик движения стрелками
     * Запрещает перемещение заблокированных элементов и презентационных фреймов
     */
    const createArrowHandler = useCallback((dx, dy) => () => {
        const stage = store.get(stageRefAtom);
        if (!canEdit || !stage)
            return;
        if (!selectedIds.length)
            return;
        const elementsMap = collectElements(boardState);
        const movableIds = selectedIds.filter((id) => {
            const element = elementsMap.get(id);
            return element && !element.locked;
        });
        if (movableIds.length === 0) {
            notifyActionBlocked('move');
            return;
        }
        return moveSelectedElements(movableIds, visibleElements, boardState, gridSize, snapToGrid, stage, setBoardState, debouncedMoveSend, dx, dy);
    }, [
        canEdit,
        selectedIds,
        visibleElements,
        boardState,
        gridSize,
        snapToGrid,
        store,
        setBoardState,
        debouncedMoveSend,
        notifyActionBlocked,
    ]);
    /**
     * Обработчик переключения locked состояния элементов (только GM)
     */
    const onToggleLock = useCallback(() => {
        if (role !== 'GM')
            return;
        if (!selectedIds.length)
            return;
        const currentLockState = selectionLockState(boardState, selectedIds);
        // Если все элементы заблокированы, разблокируем, иначе блокируем
        const newLockState = currentLockState === true ? false : true;
        const payload = selectedIds.map((id) => ({
            id,
            locked: newLockState,
        }));
        setBoardState(draftStateAfterUpdate(boardState, payload));
        send({ type: 'element.update', payload });
    }, [role, selectedIds, boardState, setBoardState, send]);
    /**
     * Обработчик группировки элементов
     * Группирует все выбранные элементы в одну группу
     */
    const onGroup = useCallback(() => {
        if (!canEdit)
            return;
        if (!selectedIds.length)
            return;
        const elementsMap = collectElements(boardState);
        const elementsToGroup = selectedIds
            .map((id) => elementsMap.get(id))
            .filter((el) => el !== undefined && !el.locked && !el.isPresentationFrame);
        if (elementsToGroup.length < 2)
            return; // Нужно минимум 2 элемента для группировки
        // Генерируем новый ID группы
        const newGroupId = generateGroupId();
        // Собираем все ID элементов, которые нужно сгруппировать
        // Если некоторые элементы уже в группах, включаем все элементы из этих групп
        const allElementIds = new Set();
        for (const element of elementsToGroup) {
            allElementIds.add(element.id);
            const existingGroupId = getElementGroupId(element);
            if (existingGroupId) {
                const groupElements = getGroupElements(boardState, existingGroupId);
                for (const groupElement of groupElements) {
                    allElementIds.add(groupElement.id);
                }
            }
        }
        // Обновляем все элементы, присваивая им новый groupId
        const payload = Array.from(allElementIds).map((id) => {
            var _a;
            return ({
                id,
                props: Object.assign(Object.assign({}, (_a = elementsMap.get(id)) === null || _a === void 0 ? void 0 : _a.props), { groupId: newGroupId }),
            });
        });
        setBoardState(draftStateAfterUpdate(boardState, payload));
        send({ type: 'element.update', payload });
    }, [canEdit, selectedIds, boardState, setBoardState, send]);
    /**
     * Обработчик разгруппировки элементов
     * Удаляет группировку у всех выбранных элементов
     */
    const onUngroup = useCallback(() => {
        var _a;
        if (!canEdit)
            return;
        if (!selectedIds.length)
            return;
        const elementsMap = collectElements(boardState);
        const elementsToUngroup = selectedIds
            .map((id) => elementsMap.get(id))
            .filter((el) => el !== undefined && !el.locked && !el.isPresentationFrame);
        if (elementsToUngroup.length === 0)
            return;
        // Собираем все группы, к которым принадлежат выбранные элементы
        const groupIds = new Set();
        for (const element of elementsToUngroup) {
            const groupId = getElementGroupId(element);
            if (groupId) {
                groupIds.add(groupId);
            }
        }
        if (groupIds.size === 0)
            return; // Нет групп для разгруппировки
        // Находим все элементы из всех найденных групп
        const allElementIds = new Set();
        for (const groupId of groupIds) {
            const groupElements = getGroupElements(boardState, groupId);
            for (const groupElement of groupElements) {
                allElementIds.add(groupElement.id);
            }
        }
        // Удаляем groupId у всех элементов этих групп
        const payload = Array.from(allElementIds).map((id) => {
            const element = elementsMap.get(id);
            if (!element)
                return { id };
            const currentProps = element.props || {};
            return {
                id,
                props: Object.assign(Object.assign({}, currentProps), { groupId: null }),
            };
        });
        // Обновляем состояние и отправляем на сервер
        // Важно: draftStateAfterUpdate объединяет props, поэтому нужно явно удалить groupId
        const newState = draftStateAfterUpdate(boardState, payload);
        // Дополнительно убеждаемся, что groupId удален из всех элементов
        for (const layer of newState.layers) {
            for (const el of layer.elements) {
                if (allElementIds.has(el.id) && ((_a = el.props) === null || _a === void 0 ? void 0 : _a.groupId)) {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const _b = el.props, { groupId } = _b, propsWithoutGroupId = __rest(_b, ["groupId"]);
                    el.props = propsWithoutGroupId;
                }
            }
        }
        setBoardState(newState);
        send({ type: 'element.update', payload });
    }, [canEdit, selectedIds, boardState, setBoardState, send]);
    // Регистрируем горячие клавиши
    useHotkeys({
        onEscape,
        onZIndexUp,
        onZIndexDown,
        onDelete,
        onCopy,
        onPasteElements,
        onPasteText,
        onPasteImage,
        onPasteImages,
        onUndo,
        onRedo,
        onZoomIn: zoomIn,
        onZoomOut: zoomOut,
        onArrowUp: createArrowHandler(0, -1),
        onArrowDown: createArrowHandler(0, 1),
        onArrowLeft: createArrowHandler(-1, 0),
        onArrowRight: createArrowHandler(1, 0),
        onToggleLock,
        onGroup,
        onUngroup,
    });
    return {
        uploadModalOpen,
        uploadDefaultFile,
        onUploadModalClose,
        onFormUploaded,
        multiUploadModalOpen,
        uploadDefaultFiles,
        onMultiUploadModalClose,
        onMultiFormUploaded,
        isAltPressed,
        isShiftPressed,
        isControlPressed,
        isMetaPressed,
        onPasteImage,
        onPasteImages,
        onPasteText,
    };
};
